/**
 * Modal "Nova conversa" — busca um contato do CRM e abre (ou reusa) a thread.
 * Reaproveita getCrmContacts (mesmos contacts do Comercial; não duplica).
 *
 * Canal (Fase B): Interno (padrão) ou WhatsApp. No WhatsApp o operador escolhe a
 * instância conectada que envia; a thread liga o contato pelo telefone (jid).
 */

import { useEffect, useRef, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { getCrmContacts, listWhatsappInstances, listWhatsappMetaAccounts } from '../../db/api.js'
import { useApp } from '../../context/AppContext.jsx'
import { avatarColor, initials, phoneToJid } from './atendimentoKit.jsx'

export default function NewConversationModal({ onClose, onPick }) {
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null

  const [channel, setChannel] = useState('interno')
  const [busca, setBusca] = useState('')
  const [contatos, setContatos] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  // conexões WhatsApp do client: Evolution (só 'open' envia) e WhatsApp Oficial (Meta, ativas).
  // Cada uma vira { key, provider, name, label }; `instance` guarda a key escolhida (#28).
  const [instances, setInstances] = useState([])
  const [instance, setInstance] = useState('')
  const [instLoading, setInstLoading] = useState(false)

  const inputRef = useRef(null)
  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    let alive = true
    setLoading(true); setErr(null)
    const t = setTimeout(async () => {
      try {
        const rows = await getCrmContacts(busca || undefined)
        if (alive) setContatos(Array.isArray(rows) ? rows : (rows?.items || []))
      } catch (e) { if (alive) setErr(e.message) }
      finally { if (alive) setLoading(false) }
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [busca])

  // carrega instâncias só quando o operador escolhe o canal WhatsApp
  useEffect(() => {
    if (channel !== 'whatsapp') return
    let alive = true
    setInstLoading(true)
    ;(async () => {
      try {
        // refresh=true sincroniza o estado AO VIVO na Evolution antes de filtrar
        // (pega instância conectada fora do app, cujo status no banco ficou stale).
        const [rows, metaRows] = await Promise.all([
          listWhatsappInstances({ refresh: true }),
          listWhatsappMetaAccounts().catch(() => []),
        ])
        const evolution = (Array.isArray(rows) ? rows : [])
          .filter((i) => i.status === 'open' && (!clientId || i.client_id === clientId))
          .map((i) => ({
            key: `evolution:${i.instance_name}`, provider: 'evolution', name: i.instance_name,
            label: i.phone ? `${i.phone} · ${i.instance_name}` : i.instance_name,
          }))
        const official = (Array.isArray(metaRows) ? metaRows : [])
          .filter((a) => a.status === 'active' && (!clientId || a.client_id === clientId))
          .map((a) => ({
            key: `meta:${a.id}`, provider: 'meta', name: a.id,
            label: `${a.display_phone_number || a.verified_name || a.phone_number_id} · Oficial`,
          }))
        const open = [...evolution, ...official]
        if (!alive) return
        setInstances(open)
        setInstance((prev) => prev || open[0]?.key || '')
      } catch { if (alive) setInstances([]) }
      finally { if (alive) setInstLoading(false) }
    })()
    return () => { alive = false }
  }, [channel, clientId])

  const isWa = channel === 'whatsapp'
  const waReady = !isWa || !!instance

  const pick = (c) => {
    if (!isWa) { onPick(c, { channel: 'interno' }); return }
    const jid = phoneToJid(c.telefone || c.whatsapp_id)
    if (!jid) return  // contato sem telefone: item fica desabilitado
    const route = instances.find((i) => i.key === instance)
    if (!route) return
    onPick(c, {
      channel: 'whatsapp',
      wa_provider: route.provider,
      wa_instance_name: route.name,
      wa_remote_jid: jid,
    })
  }

  return (
    <div className="at-modal-overlay" onClick={onClose}>
      <div className="at-modal" onClick={(e) => e.stopPropagation()}>
        <div className="at-modal-head">
          <h3>Nova conversa</h3>
          <button className="at-icon-btn" onClick={onClose} aria-label="Fechar">
            <Icon n="close" s={16} />
          </button>
        </div>

        {/* canal */}
        <div className="at-tabs at-modal-tabs">
          <button
            className={`at-tab${channel === 'interno' ? ' is-active' : ''}`}
            onClick={() => setChannel('interno')}
          >Interno</button>
          <button
            className={`at-tab${channel === 'whatsapp' ? ' is-active' : ''}`}
            onClick={() => setChannel('whatsapp')}
          ><Icon n="whatsapp" s={13} /> WhatsApp</button>
        </div>

        {/* seletor de instância (WhatsApp) */}
        {isWa && (
          <div className="at-modal-inst">
            {instLoading && <span className="at-muted">Carregando conexões…</span>}
            {!instLoading && instances.length === 0 && (
              <span className="at-error">
                Nenhuma conexão WhatsApp ativa. Conecte um número no Perfil › Integrações › WhatsApp.
              </span>
            )}
            {!instLoading && instances.length > 0 && (
              <label className="at-field">
                <span>Enviar por</span>
                <select value={instance} onChange={(e) => setInstance(e.target.value)}>
                  {instances.map((i) => (
                    <option key={i.key} value={i.key}>{i.label}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        <div className="at-modal-search">
          <Icon n="search" s={15} />
          <input
            ref={inputRef}
            placeholder="Buscar contato por nome, e-mail ou telefone…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div className="at-modal-list">
          {loading && <div className="at-muted at-pad">Carregando…</div>}
          {err && <div className="at-error at-pad">{err}</div>}
          {!loading && !err && contatos.length === 0 && (
            <div className="at-muted at-pad">Nenhum contato encontrado.</div>
          )}
          {contatos.map((c) => {
            const semFone = isWa && !phoneToJid(c.telefone || c.whatsapp_id)
            const disabled = !waReady || semFone
            return (
              <button
                key={c.id}
                className="at-modal-item"
                disabled={disabled}
                onClick={() => pick(c)}
              >
                <span className="at-avatar" style={{ background: avatarColor(c.nome || c.id) }}>
                  {initials(c.nome)}
                </span>
                <span className="at-modal-item-body">
                  <span className="at-modal-item-name">{c.nome || 'Sem nome'}</span>
                  <span className="at-modal-item-sub">
                    {semFone ? 'Sem telefone p/ WhatsApp' : (c.email || c.telefone || '—')}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
